import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ShieldStar, WarningCircle, CheckCircle, Plus, Trash, PencilSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const KIND_OPTIONS = [
  { value: "white_card",         label: "White Card (Construction Induction)" },
  { value: "police_check",       label: "National Police Check" },
  { value: "wwcc",               label: "Working With Children Check" },
  { value: "first_aid",          label: "First Aid Certificate" },
  { value: "working_at_heights", label: "Working at Heights" },
  { value: "dc_induction",       label: "Data-Centre Induction" },
  { value: "right_to_work",      label: "Right to Work in Australia" },
  { value: "electrical_license", label: "Electrical License" },
  { value: "other",              label: "Other" },
];

const REASON_LABEL = {
  missing: "missing",
  unverified: "not verified by admin",
  expired: "expired",
};

const EMPTY_FORM = {
  kind: "white_card", kind_label: "",
  reference_number: "", issuing_authority: "", state: "",
  issued_date: "", expiry_date: "", notes: "",
};

export default function SiteAccess() {
  const [summary, setSummary] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [credsByPerson, setCredsByPerson] = useState({}); // pid -> [creds]
  const [filter, setFilter] = useState("all"); // all | ready | not_ready
  const [dialog, setDialog] = useState(null);   // { pid, personName, credential? }
  const [form, setForm] = useState(EMPTY_FORM);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/site-access/summary"),
        api.get("/site-access/expiring?days=90"),
      ]);
      setSummary(s.data);
      setExpiring(e.data);
    } catch { toast.error("Failed to load site-access data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const loadPersonCreds = async (pid) => {
    try {
      const r = await api.get(`/personnel/${pid}/site-access`);
      setCredsByPerson((prev) => ({ ...prev, [pid]: r.data }));
    } catch { toast.error("Failed to load credentials"); }
  };

  const toggleExpanded = (pid) => {
    const next = { ...expanded, [pid]: !expanded[pid] };
    setExpanded(next);
    if (next[pid] && !credsByPerson[pid]) loadPersonCreds(pid);
  };

  const openAdd = (pid, personName) => { setDialog({ pid, personName }); setForm(EMPTY_FORM); };
  const openEdit = (pid, personName, credential) => {
    setDialog({ pid, personName, credential });
    setForm({
      kind: credential.kind, kind_label: credential.kind === "other" ? credential.kind_label : "",
      reference_number: credential.reference_number || "",
      issuing_authority: credential.issuing_authority || "",
      state: credential.state || "",
      issued_date: credential.issued_date || "",
      expiry_date: credential.expiry_date || "",
      notes: credential.notes || "",
    });
  };

  const saveCredential = async () => {
    if (form.kind === "other" && !form.kind_label.trim()) { toast.error("Provide a label for 'Other' credentials"); return; }
    try {
      const payload = {
        ...form,
        issued_date: form.issued_date || null,
        expiry_date: form.expiry_date || null,
      };
      if (dialog.credential) {
        await api.patch(`/personnel/${dialog.pid}/site-access/${dialog.credential.id}`, payload);
        toast.success("Credential updated — verification reset, admin must re-verify");
      } else {
        await api.post(`/personnel/${dialog.pid}/site-access`, payload);
        toast.success("Credential added");
      }
      setDialog(null);
      await loadPersonCreds(dialog.pid);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const toggleVerify = async (pid, cred) => {
    try {
      await api.post(`/personnel/${pid}/site-access/${cred.id}/verify`);
      await loadPersonCreds(pid);
      loadAll();
    } catch { toast.error("Verify toggle failed"); }
  };

  const removeCredential = async (pid, cred) => {
    if (!window.confirm(`Remove ${cred.kind_label} for this person?`)) return;
    try {
      await api.delete(`/personnel/${pid}/site-access/${cred.id}`);
      await loadPersonCreds(pid);
      loadAll();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    if (filter === "ready") return summary.filter((s) => s.site_ready);
    if (filter === "not_ready") return summary.filter((s) => !s.site_ready);
    return summary;
  }, [summary, filter]);

  const readyCount = summary.filter((s) => s.site_ready).length;
  const notReadyCount = summary.length - readyCount;

  if (loading) return <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Loading site-access data…</div>;

  return (
    <div>
      <PageHeader
        overline="HR · Compliance"
        title="Site Access Register"
        subtitle="Track White Card, Police Check, DC inductions and other credentials. A person can be dispatched to a data centre only when they hold a verified & non-expired White Card AND Police Check."
        actions={
          <Button data-testid="refresh-site-access" onClick={loadAll} variant="outline" className="border-slate-300 bg-white text-[#0F1E38] font-mono uppercase tracking-wider text-xs">
            <ArrowsClockwise size={14} className="mr-2" /> Refresh
          </Button>
        }
      />

      {/* Stat pills */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <button onClick={() => setFilter("all")} data-testid="filter-all"
          className={`text-left border rounded-lg p-4 transition ${filter === "all" ? "border-[#0F1E38] bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500">Total personnel</div>
          <div className="text-2xl font-semibold text-[#0F1E38] mt-1">{summary.length}</div>
        </button>
        <button onClick={() => setFilter("ready")} data-testid="filter-ready"
          className={`text-left border rounded-lg p-4 transition ${filter === "ready" ? "border-emerald-500 bg-emerald-50/60" : "border-slate-200 bg-white hover:border-emerald-200"}`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-700 flex items-center gap-1"><CheckCircle size={12} weight="bold" /> Site-ready</div>
          <div className="text-2xl font-semibold text-emerald-700 mt-1">{readyCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">White Card + Police Check verified & valid</div>
        </button>
        <button onClick={() => setFilter("not_ready")} data-testid="filter-not-ready"
          className={`text-left border rounded-lg p-4 transition ${filter === "not_ready" ? "border-red-400 bg-red-50/60" : "border-slate-200 bg-white hover:border-red-200"}`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-red-700 flex items-center gap-1"><WarningCircle size={12} weight="bold" /> Not cleared for site</div>
          <div className="text-2xl font-semibold text-red-700 mt-1">{notReadyCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Missing / unverified / expired credentials</div>
        </button>
      </div>

      {/* Expiring soon banner */}
      {expiring.length > 0 && (
        <div className="mb-6 border border-amber-200 bg-amber-50/60 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <WarningCircle size={16} className="text-amber-700" weight="bold" />
            <span className="text-sm font-semibold text-[#0F1E38]">Expiring in the next 90 days ({expiring.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            {expiring.slice(0, 12).map((c) => (
              <div key={c.credential_id} className={`flex items-center gap-2 p-2 rounded border ${c.expired ? "bg-red-50 border-red-200" : "bg-white border-amber-200"}`}>
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${c.expired ? "bg-red-100 text-red-700" : c.days_until_expiry <= 30 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                  {c.expired ? "EXPIRED" : `${c.days_until_expiry}d`}
                </span>
                <span className="text-[#0F1E38] font-medium">{c.personnel_name}</span>
                <span className="text-slate-500">·</span>
                <span className="text-slate-600">{c.kind_label}</span>
                <span className="ml-auto text-slate-400 font-mono">{c.expiry_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-person table */}
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Person</th>
              <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Region</th>
              <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Credentials</th>
              <th className="text-right px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <React.Fragment key={p.personnel_id}>
                <tr data-testid={`site-row-${p.personnel_id}`} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => toggleExpanded(p.personnel_id)}>
                  <td className="px-4 py-3 text-[#0F1E38] font-medium">{p.personnel_name}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{p.region || "—"}</td>
                  <td className="px-4 py-3">
                    {p.site_ready ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-mono uppercase tracking-wider">
                        <CheckCircle size={12} weight="fill" /> Ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] font-mono uppercase tracking-wider" title={p.blockers.map((b) => `${b.label}: ${REASON_LABEL[b.reason]}`).join(" · ")}>
                        <WarningCircle size={12} weight="fill" /> Not cleared
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-slate-500">
                      {p.credential_count === 0 ? <span className="italic">no credentials on file</span> : `${p.credential_count} on file`}
                      {p.blockers.length > 0 && (
                        <div className="mt-1 text-red-600 text-[11px]">
                          {p.blockers.map((b) => `${b.label} · ${REASON_LABEL[b.reason]}`).join(" · ")}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm" variant="outline"
                      data-testid={`add-cred-${p.personnel_id}`}
                      onClick={(e) => { e.stopPropagation(); openAdd(p.personnel_id, p.personnel_name); }}
                      className="h-7 text-[11px] border-slate-300"
                    >
                      <Plus size={12} className="mr-1" /> Add
                    </Button>
                  </td>
                </tr>
                {expanded[p.personnel_id] && (
                  <tr>
                    <td colSpan={5} className="bg-slate-50/50 px-4 py-3 border-b border-slate-200">
                      {(credsByPerson[p.personnel_id] || []).length === 0 ? (
                        <div className="text-xs text-slate-500 italic py-2">No credentials on file yet.</div>
                      ) : (
                        <div className="space-y-2">
                          {credsByPerson[p.personnel_id].map((c) => (
                            <div key={c.id} data-testid={`cred-row-${c.id}`} className="grid grid-cols-12 gap-2 items-center text-xs bg-white border border-slate-200 rounded p-2">
                              <div className="col-span-4">
                                <div className="font-semibold text-[#0F1E38]">{c.kind_label}</div>
                                <div className="text-slate-500 font-mono">{c.reference_number || "—"}</div>
                              </div>
                              <div className="col-span-3 text-slate-500">
                                <div className="text-[10px] uppercase tracking-wider">Issued</div>
                                <div className="font-mono">{c.issued_date || "—"}</div>
                              </div>
                              <div className="col-span-2 text-slate-500">
                                <div className="text-[10px] uppercase tracking-wider">Expires</div>
                                <div className={`font-mono ${c.expired ? "text-red-700 font-semibold" : ""}`}>{c.expiry_date || "permanent"}</div>
                              </div>
                              <div className="col-span-1">
                                {c.verified ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-mono" title={c.verified_by}>
                                    <CheckCircle size={11} weight="fill" /> Verified
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-amber-700 text-[10px] font-mono">
                                    <WarningCircle size={11} weight="bold" /> Pending
                                  </span>
                                )}
                              </div>
                              <div className="col-span-2 flex gap-1 justify-end">
                                <Button size="sm" variant="outline" data-testid={`verify-${c.id}`} onClick={() => toggleVerify(p.personnel_id, c)} className={`h-7 text-[10px] px-2 ${c.verified ? "border-slate-300" : "border-emerald-400 text-emerald-700"}`}>
                                  {c.verified ? "Un-verify" : "Verify"}
                                </Button>
                                <Button size="sm" variant="ghost" data-testid={`edit-${c.id}`} onClick={() => openEdit(p.personnel_id, p.personnel_name, c)} className="h-7 w-7 p-0"><PencilSimple size={12} /></Button>
                                <Button size="sm" variant="ghost" data-testid={`remove-${c.id}`} onClick={() => removeCredential(p.personnel_id, c)} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"><Trash size={12} /></Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg">
          <DialogHeader>
            <DialogTitle>
              <ShieldStar size={18} className="inline mr-2 text-[#B7791F]" weight="bold" />
              {dialog?.credential ? "Edit" : "Add"} Credential — {dialog?.personName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Credential Type</Label>
              <select data-testid="cred-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full h-10 px-3 rounded border border-slate-300 bg-white text-sm">
                {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {form.kind === "other" && (
              <div>
                <Label>Custom label <span className="text-red-500">*</span></Label>
                <Input data-testid="cred-kind-label" value={form.kind_label} onChange={(e) => setForm({ ...form, kind_label: e.target.value })} placeholder="e.g. Traffic Controller Ticket" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Reference / ID number</Label>
                <Input data-testid="cred-ref" value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="e.g. WC-12345" />
              </div>
              <div>
                <Label>State (AU)</Label>
                <select data-testid="cred-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1 w-full h-10 px-3 rounded border border-slate-300 bg-white text-sm">
                  <option value="">—</option>
                  {["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Issuing authority</Label>
              <Input data-testid="cred-authority" value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} placeholder="e.g. SafeWork NSW, Victoria Police, NEXTDC" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Issued date</Label>
                <Input type="date" data-testid="cred-issued" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} />
              </div>
              <div>
                <Label>Expiry date <span className="text-slate-400 text-xs">(blank = permanent)</span></Label>
                <Input type="date" data-testid="cred-expiry" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} data-testid="cred-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes — e.g. sighted photo copy on 12 Aug 2026" />
            </div>
            {dialog?.credential && dialog.credential.verified && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Note: saving changes will reset this credential's verified status — admin/HR will need to re-verify.
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog(null)} className="border-slate-300">Cancel</Button>
            <Button data-testid="save-cred" onClick={saveCredential} className="bg-[#0F1E38] hover:bg-[#1E3A5F] text-white">{dialog?.credential ? "Save changes" : "Add credential"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
